// Orchestrates installed plugins: validates manifests, indexes their declarative
// contributions (so commands/views appear BEFORE any code runs), and activates a
// plugin lazily through the runtime factory, wiring a PluginConnection so the
// plugin's `slate.*` calls reach the host. Trust-tier-agnostic: it only knows
// the factory.

import {
  validateManifest,
  type TreeItem,
  type FoldRange,
  type PermissionCapability,
  type PermissionState,
} from '@slate/plugin-api';
import { PluginConnection, type PluginHostServices } from './rpcHost';
import type { InstalledPlugin, PluginRuntimeFactory, PluginRuntimeHandle } from './runtime';
import type { KillSwitch } from './integrity/killSwitch';

/** Sink for plugin fold providers (the HostFoldRegistry). PluginHost tells it to
 *  drop a plugin's providers on disable / uninstall; introspection via `list()`.
 *  Optional so the host works (with no folding) when none is attached. */
export interface FoldSink {
  removePlugin(pluginId: string): void;
  list(): string[];
}

/** Thrown when a plugin is refused for integrity or revocation reasons. */
export class PluginRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PluginRefusedError';
  }
}

/** Runs before the runtime factory spawns a worker; throw to refuse the load. */
export type LoadGuard = (plugin: InstalledPlugin) => void | Promise<void>;

export interface PluginHostGuards {
  killSwitch?: KillSwitch;
  loadGuard?: LoadGuard;
}

export interface DeclaredCommand {
  pluginId: string;
  id: string;
  title: string;
  category?: string;
  icon?: string;
  keybinding?: string;
}

export interface DeclaredView {
  pluginId: string;
  id: string;
  container: string;
  title: string;
  kind: 'tree' | 'surface';
}

export interface DeclaredSurface {
  pluginId: string;
  id: string;
  path: string;
  type: 'panel' | 'modal';
}

export interface DeclaredTool {
  pluginId: string;
  id: string;
  title: string;
  icon: string;
}

export interface SurfaceMessageEvent {
  pluginId: string;
  surfaceId: string;
  message: unknown;
}

export interface DeclaredActivityBarItem {
  pluginId: string;
  id: string;
  title: string;
  icon: string;
  view: string;
}

export interface DeclaredTopBarAction {
  pluginId: string;
  id: string;
  icon: string;
  title: string;
  command: string;
  placement: 'beforeHelp';
}

interface ActivePlugin {
  handle: PluginRuntimeHandle;
  conn: PluginConnection;
}

export class PluginHost {
  private readonly installed = new Map<string, InstalledPlugin>();
  private readonly active = new Map<string, ActivePlugin>();
  private readonly activating = new Map<string, Promise<void>>();
  private readonly surfaceSubs = new Set<(e: SurfaceMessageEvent) => void>();
  // Enabled-state as the COMPLEMENT of a disabled set: a freshly-installed
  // plugin is enabled with no bookkeeping. Disable deactivates and hides
  // contributions; uninstall drops the plugin entirely.
  private readonly disabled = new Set<string>();
  // Declarative-contribution change notification: install/uninstall/enable/
  // disable change the set of visible commands/views, so consumers (the command
  // palette) can re-derive. `contributionRev` is a monotonic version for
  // useSyncExternalStore snapshots.
  private readonly contributionSubs = new Set<() => void>();
  private contributionRev = 0;
  // Fold providers a plugin registered at runtime live in the HostFoldRegistry
  // (the core fold registry bridge). Held here only so disable/uninstall can drop
  // them. See the "Fold providers" block below.
  private foldSink: FoldSink | null = null;

  constructor(
    private readonly factory: PluginRuntimeFactory,
    private readonly services: PluginHostServices,
    private readonly guards: PluginHostGuards = {},
  ) {}

  /** Validate + register a plugin. Declarative contributions become visible now;
   *  no plugin code runs until activation. */
  install(plugin: InstalledPlugin): void {
    const res = validateManifest(plugin.manifest);
    if (!res.ok) throw new Error(`invalid plugin manifest: ${res.errors.join('; ')}`);
    const { id, version } = res.manifest;
    if (this.guards.killSwitch?.isRevoked(id, version)) {
      throw new PluginRefusedError(`plugin "${id}" is revoked and will not be loaded`);
    }
    this.installed.set(id, plugin);
    this.emitContributionChange();
  }

  list(): InstalledPlugin[] {
    return [...this.installed.values()];
  }

  listCommands(): DeclaredCommand[] {
    const out: DeclaredCommand[] = [];
    for (const p of this.installed.values()) {
      if (this.disabled.has(p.manifest.id)) continue;
      for (const c of p.manifest.contributes?.commands ?? []) {
        out.push({
          pluginId: p.manifest.id,
          id: c.id,
          title: c.title,
          category: c.category,
          icon: c.icon,
          keybinding: c.keybinding,
        });
      }
    }
    return out;
  }

  listViews(): DeclaredView[] {
    const out: DeclaredView[] = [];
    for (const p of this.installed.values()) {
      if (this.disabled.has(p.manifest.id)) continue;
      for (const v of p.manifest.contributes?.views ?? []) {
        out.push({
          pluginId: p.manifest.id,
          id: v.id,
          container: v.container,
          title: v.title,
          kind: v.kind,
        });
      }
    }
    return out;
  }

  listActivityBarItems(): DeclaredActivityBarItem[] {
    const out: DeclaredActivityBarItem[] = [];
    for (const p of this.installed.values()) {
      if (this.disabled.has(p.manifest.id)) continue;
      for (const a of p.manifest.contributes?.activityBar ?? []) {
        out.push({ pluginId: p.manifest.id, id: a.id, title: a.title, icon: a.icon, view: a.view });
      }
    }
    return out;
  }

  listTopBarActions(): DeclaredTopBarAction[] {
    const out: DeclaredTopBarAction[] = [];
    for (const p of this.installed.values()) {
      if (this.disabled.has(p.manifest.id)) continue;
      for (const a of p.manifest.contributes?.topBarActions ?? []) {
        out.push({
          pluginId: p.manifest.id,
          id: a.id,
          icon: a.icon,
          title: a.title,
          command: a.command,
          placement: a.placement,
        });
      }
    }
    return out;
  }

  listSurfaces(): DeclaredSurface[] {
    const out: DeclaredSurface[] = [];
    for (const p of this.installed.values()) {
      if (this.disabled.has(p.manifest.id)) continue;
      for (const s of p.manifest.contributes?.surfaces ?? []) {
        out.push({ pluginId: p.manifest.id, id: s.id, path: s.path, type: s.type });
      }
    }
    return out;
  }

  listTools(): DeclaredTool[] {
    const out: DeclaredTool[] = [];
    for (const p of this.installed.values()) {
      if (this.disabled.has(p.manifest.id)) continue;
      for (const t of p.manifest.contributes?.tools ?? []) {
        out.push({ pluginId: p.manifest.id, id: t.id, title: t.title, icon: t.icon });
      }
    }
    return out;
  }

  /** Host-internal: the surface service calls this to fan a worker push out to
   *  every rendered SurfaceHost. */
  emitSurfaceMessage(pluginId: string, surfaceId: string, message: unknown): void {
    const e: SurfaceMessageEvent = { pluginId, surfaceId, message };
    for (const cb of [...this.surfaceSubs]) cb(e);
  }

  /** Subscribe to worker-to-surface messages; returns an unsubscribe. */
  onSurfaceMessage(cb: (e: SurfaceMessageEvent) => void): () => void {
    this.surfaceSubs.add(cb);
    return () => void this.surfaceSubs.delete(cb);
  }

  /** Route an inbound iframe message to the owning plugin's worker (lazy). */
  async deliverSurfaceMessage(pluginId: string, surfaceId: string, message: unknown): Promise<void> {
    await this.activate(pluginId);
    await this.active.get(pluginId)!.conn.deliverSurfaceMessage(surfaceId, message);
  }

  isActive(pluginId: string): boolean {
    return this.active.has(pluginId);
  }

  isEnabled(pluginId: string): boolean {
    return !this.disabled.has(pluginId);
  }

  /** Boot-time: mark plugins disabled without deactivating or emitting. Nothing
   *  is active yet (pre-render), so there is nothing to tear down and no
   *  subscriber to notify; the first render derives the correct hidden state. */
  restoreDisabled(ids: readonly string[]): void {
    for (const id of ids) this.disabled.add(id);
  }

  /** Subscribe to declarative-contribution changes (install/uninstall/enable/
   *  disable). Returns an unsubscribe. Used by the command palette to re-derive. */
  onDidChangeContributions(cb: () => void): () => void {
    this.contributionSubs.add(cb);
    return () => void this.contributionSubs.delete(cb);
  }

  /** Monotonic version of the declarative-contribution set, for snapshots. */
  contributionVersion(): number {
    return this.contributionRev;
  }

  private emitContributionChange(): void {
    this.contributionRev += 1;
    for (const cb of [...this.contributionSubs]) cb();
  }

  /** Disable => deactivate + hide contributions. Enable => restore (lazy). */
  async setEnabled(pluginId: string, enabled: boolean): Promise<void> {
    if (enabled) {
      this.disabled.delete(pluginId);
      this.emitContributionChange();
      return;
    }
    this.disabled.add(pluginId);
    await this.deactivate(pluginId);
    this.foldSink?.removePlugin(pluginId);
    this.emitContributionChange();
  }

  /** Remove a plugin: deactivate, drop it from installed, clear disabled flag. */
  async uninstall(pluginId: string): Promise<void> {
    await this.deactivate(pluginId);
    this.foldSink?.removePlugin(pluginId);
    this.installed.delete(pluginId);
    this.disabled.delete(pluginId);
    this.emitContributionChange();
  }

  /** Run the SAME integrity gate the load path uses (kill switch + load guard)
   *  with NO activation, so a tampered/revoked/unsigned plugin cannot install. */
  async verifyForInstall(plugin: InstalledPlugin): Promise<void> {
    const { id, version } = plugin.manifest;
    if (this.guards.killSwitch?.isRevoked(id, version)) {
      throw new PluginRefusedError(`plugin "${id}" is revoked and will not be installed`);
    }
    if (this.guards.loadGuard) await this.guards.loadGuard(plugin);
  }

  /** Spin up the plugin (idempotent). Wires the connection BEFORE running the
   *  plugin's activate, so the plugin's registrations are observed. */
  activate(pluginId: string): Promise<void> {
    if (this.active.has(pluginId)) return Promise.resolve();
    const inflight = this.activating.get(pluginId);
    if (inflight) return inflight;
    const plugin = this.installed.get(pluginId);
    if (!plugin) return Promise.reject(new Error(`plugin "${pluginId}" is not installed`));
    if (this.disabled.has(pluginId)) {
      return Promise.reject(new PluginRefusedError(`plugin "${pluginId}" is disabled`));
    }

    const run = (async () => {
      const { id, version } = plugin.manifest;
      if (this.guards.killSwitch?.isRevoked(id, version)) {
        throw new PluginRefusedError(`plugin "${id}" is revoked and will not be loaded`);
      }
      if (this.guards.loadGuard) await this.guards.loadGuard(plugin);
      const handle = await this.factory(plugin);
      const conn = new PluginConnection(handle.channel, pluginId, this.services);
      try {
        await conn.activatePlugin();
      } catch (e) {
        conn.dispose();
        await handle.dispose();
        throw e;
      }
      this.active.set(pluginId, { handle, conn });
    })();

    this.activating.set(pluginId, run);
    return run.finally(() => this.activating.delete(pluginId));
  }

  async runCommand(pluginId: string, commandId: string, args: unknown[] = []): Promise<unknown> {
    await this.activate(pluginId);
    return this.active.get(pluginId)!.conn.invokeCommand(commandId, args);
  }

  async viewChildren(pluginId: string, viewId: string, parentId?: string): Promise<TreeItem[]> {
    await this.activate(pluginId);
    return this.active.get(pluginId)!.conn.getViewChildren(viewId, parentId);
  }

  /** Forward a grant/revocation to the running plugin (for live reactivity). */
  notifyPermissionChanged(
    pluginId: string,
    capability: PermissionCapability,
    state: PermissionState,
  ): void {
    this.active.get(pluginId)?.conn.emitPermissionChanged(capability, state);
  }

  async deactivate(pluginId: string): Promise<void> {
    const a = this.active.get(pluginId);
    if (!a) return;
    this.active.delete(pluginId);
    await a.conn.deactivatePlugin();
    a.conn.dispose();
    await a.handle.dispose();
  }

  // --- Fold providers ------------------------------------------------------
  // Self-contained block: a plugin registers fold providers at runtime via
  // slate.editor.registerFoldProvider; they are bridged into the core fold
  // registry by the FoldSink. The host pulls ranges by id (mirrors viewChildren)
  // and drops a plugin's providers from the sink on disable/uninstall (above).

  /** Attach the fold-provider sink (the HostFoldRegistry). */
  setFoldSink(sink: FoldSink | null): void {
    this.foldSink = sink;
  }

  /** Pull fold ranges from one of a plugin's registered fold providers,
   *  activating the plugin if needed. */
  async getFolds(
    pluginId: string,
    providerId: string,
    source: string,
    fileName: string,
  ): Promise<FoldRange[]> {
    await this.activate(pluginId);
    return this.active.get(pluginId)!.conn.getFolds(providerId, source, fileName);
  }

  /** Bridged fold-provider keys (`pluginId::providerId`), or [] with no sink. */
  listFoldProviders(): string[] {
    return this.foldSink?.list() ?? [];
  }
  // --- end Fold providers --------------------------------------------------

  async dispose(): Promise<void> {
    for (const id of [...this.active.keys()]) await this.deactivate(id);
    this.installed.clear();
  }
}
